/**
 * All node CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.node;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_NODE = "INVALIDATE_SELECTED_NODE"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_NODE
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleNodeById(id))
  } else {
    return dispatch(returnSingleNodePromise(id)); // return promise that contains node
  }
}

export const returnSingleNodePromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_NODE_WITHOUT_FETCHING"
      , id: id
      , item: getState().node.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_NODE = "REQUEST_SINGLE_NODE";
function requestSingleNode(id) {
  return {
    type: REQUEST_SINGLE_NODE
    , id
  }
}

export const RECEIVE_SINGLE_NODE = "RECEIVE_SINGLE_NODE";
function receiveSingleNode(json) {
  return {
    type: RECEIVE_SINGLE_NODE
    , id: json.node ? json.node._id : null
    , item: json.node
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleNodeById(nodeId) {
  return dispatch => {
    dispatch(requestSingleNode(nodeId))
    return callAPI(`/api/nodes/${nodeId}`)
      .then(json => dispatch(receiveSingleNode(json)))
  }
}

export const ADD_SINGLE_NODE_TO_MAP = "ADD_SINGLE_NODE_TO_MAP";
export function addSingleNodeToMap(item) {
  return {
    type: ADD_SINGLE_NODE_TO_MAP
    , item
  }
}

export const REQUEST_CREATE_NODE = "REQUEST_CREATE_NODE";
function requestCreateNode(node) {
  return {
    type: REQUEST_CREATE_NODE
    , node
  }
}

export const RECEIVE_CREATE_NODE = "RECEIVE_CREATE_NODE";
function receiveCreateNode(json) {
  return {
    type: RECEIVE_CREATE_NODE
    , id: json.node ? json.node._id : null
    , item: json.node
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateNode(data) {
  return dispatch => {
    dispatch(requestCreateNode(data))
    return callAPI('/api/nodes', 'POST', data)
      .then(json => dispatch(receiveCreateNode(json)))
  }
}

export const REQUEST_UPDATE_NODE = "REQUEST_UPDATE_NODE";
function requestUpdateNode(node) {
  return {
    type: REQUEST_UPDATE_NODE
    , id: node ? node._id : null
    , node
  }
}

export const RECEIVE_UPDATE_NODE = "RECEIVE_UPDATE_NODE";
function receiveUpdateNode(json) {
  return {
    type: RECEIVE_UPDATE_NODE
    , id: json.node ? json.node._id : null
    , item: json.node
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateNode(data) {
  return dispatch => {
    dispatch(requestUpdateNode(data))
    return callAPI(`/api/nodes/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateNode(json)))
  }
}

export const REQUEST_DELETE_NODE = "REQUEST_DELETE_NODE";
function requestDeleteNode(nodeId) {
  return {
    type: REQUEST_DELETE_NODE
    , id: nodeId
  }
}

export const RECEIVE_DELETE_NODE = "RECEIVE_DELETE_NODE";
function receiveDeleteNode(id, json) {
  return {
    type: RECEIVE_DELETE_NODE
    , id
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteNode(id))
    return callAPI(`/api/nodes/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteNode(id, json)))
  }
}


/**
 * NODE LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest nodeLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.node.lists, {});
  let list = { ...state.node.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnNodeListPromise(...listArgs));
  }
}

export const returnNodeListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.node.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_NODE_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_APPLY_LAYOUT_TO_MODULE = "REQUEST_APPLY_LAYOUT_TO_MODULE"
function requestApplyLayoutToModule(moduleId) {
  return {
    type: REQUEST_APPLY_LAYOUT_TO_MODULE
    , moduleId
  }
}

export function applyLayoutToModule(moduleId) {
  return dispatch => {
    dispatch(requestApplyLayoutToModule(moduleId))
    return callAPI(`/api/nodes/apply-layout-to-module/${moduleId}`, 'POST', {})
      .then(json => {
        // overwrite the authoring list with the new list
        return dispatch(receiveNodeList(json, ["_contentModule", moduleId]))
      })
  }
}

export const REQUEST_NODE_LIST = "REQUEST_NODE_LIST"
function requestNodeList(listArgs) {
  return {
    type: REQUEST_NODE_LIST
    , listArgs
  }
}

export const RECEIVE_NODE_LIST = "RECEIVE_NODE_LIST"
function receiveNodeList(json, listArgs) {
  return {
    type: RECEIVE_NODE_LIST
    , listArgs
    , list: json.nodes
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_NODE_TO_LIST = "ADD_NODE_TO_LIST";
export function addNodeToList(id, ...listArgs) {
  // console.log("Add node to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_NODE_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_NODE_FROM_LIST = "REMOVE_NODE_FROM_LIST"
export function removeNodeFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_NODE_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestNodeList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/nodes/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/nodes/by-[ARG1]/[ARG2]".
     *    ex: /api/nodes/by-category/:category
     *  if 2nd arg is an array, though, return "/api/nodes/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/nodes";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && listArgs[0] == "apply-layout-to-module") {
      // 2.12 - new endpoint for applying layout to a list of nodes (via oxford)
      apiTarget += `/apply-layout-to-module/${listArgs[1]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1]) && listArgs[0] == "find-shared-parent") {
      // 1.21 - special fetch that also needs array args
      apiTarget += `/get-parent-from-shared-children?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `_id=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1]) && listArgs[0] == "find-siblings") {
      // 1.21 - special fetch that also needs array args
      apiTarget += `/get-siblings?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `_id=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of nodes given an array of node id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveNodeList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_NODE_FILTER = "SET_NODE_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_NODE_FILTER
    , filter
    , listArgs
  }
}

export const SET_NODE_PAGINATION = "SET_NODE_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_NODE_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_NODE_LIST = "INVALIDATE_NODE_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_NODE_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/node/nodeActions.js